import React from "react"
import { useStaticQuery, graphql } from "gatsby"
import { Helmet, useI18next, useTranslation } from "gatsby-plugin-react-i18next"
import {
  url,
  brand,
  legalName,
  address,
  contact,
  foundingDate,
  social,
  analyticsDomain,
  geo,
} from "src/data/config"

const KNOWS_ABOUT = {
  de: [
    "Webentwicklung",
    "WordPress",
    "TYPO3",
    "Jamstack",
    "Produktkonfiguratoren",
    "React",
    "Vue.js",
    "Node.js",
    "PHP",
    "React Native",
    "Barrierefreiheit (WCAG)",
    "Schnittstellen und ERP-Anbindung",
  ],
  en: [
    "Web development",
    "WordPress",
    "TYPO3",
    "Jamstack",
    "Product configurators",
    "React",
    "Vue.js",
    "Node.js",
    "PHP",
    "React Native",
    "Web accessibility (WCAG)",
    "API and ERP integration",
  ],
}

const clean = text => (text || "").replace(/­/g, "")

export const SEO = ({ title, description, noindex = false }) => {
  const { t } = useTranslation()
  const { language, originalPath, defaultLanguage } = useI18next()
  const { site } = useStaticQuery(graphql`
    query {
      site {
        buildTime(formatString: "YYYY-MM-DD")
      }
    }
  `)
  const isEn = language === "en"
  const pageTitle = title || t("seo:title")
  const pageDescription = description || t("seo:description")
  const langPrefix = language !== defaultLanguage ? `/${language}` : ""
  const pageUrl = `${url}${langPrefix}${originalPath}`
  const ogImage = `${url}/og-image.jpg`
  const services = t("services:items", { returnObjects: true })
  const serviceItems = Array.isArray(services) ? services : []

  const businessId = `${url}/#business`
  const personId = `${url}/#person`
  const websiteId = `${url}/#website`
  const postalAddress = {
    "@type": "PostalAddress",
    streetAddress: address.street,
    addressLocality: address.city,
    postalCode: address.zipCode,
    addressRegion: address.region,
    addressCountry: address.country,
  }

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": websiteId,
        url: `${url}/`,
        name: brand,
        inLanguage: ["de", "en"],
        publisher: { "@id": businessId },
      },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: pageTitle,
        description: pageDescription,
        inLanguage: language,
        isPartOf: { "@id": websiteId },
        about: { "@id": businessId },
        primaryImageOfPage: ogImage,
        dateModified: site.buildTime,
      },
      {
        "@type": "ProfessionalService",
        "@id": businessId,
        name: brand,
        alternateName: "medienkram",
        url: `${url}/`,
        image: ogImage,
        logo: `${url}/logo-medienkram.png`,
        description: t("seo:description"),
        telephone: contact.phone,
        email: contact.email,
        priceRange: "$$",
        foundingDate,
        founder: { "@id": personId },
        address: postalAddress,
        geo: {
          "@type": "GeoCoordinates",
          latitude: geo.latitude,
          longitude: geo.longitude,
        },
        areaServed: [
          "Rhein-Erft-Kreis",
          "Köln",
          "Nordrhein-Westfalen",
          "Deutschland",
        ],
        availableLanguage: ["de", "en"],
        sameAs: [social.linkedin, social.xing],
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: isEn ? "Services" : "Leistungen",
          itemListElement: serviceItems.map(service => ({
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: clean(service.title),
              description: service.lead,
              serviceType: Array.isArray(service.tags)
                ? service.tags.join(", ")
                : undefined,
              provider: { "@id": businessId },
              areaServed: isEn ? "Germany" : "Deutschland",
            },
          })),
        },
      },
      {
        "@type": "Person",
        "@id": personId,
        name: legalName,
        jobTitle: isEn ? "Freelance web developer" : "Freelance Webentwickler",
        url: `${url}/`,
        email: contact.email,
        telephone: contact.phone,
        worksFor: { "@id": businessId },
        homeLocation: {
          "@type": "Place",
          address: {
            "@type": "PostalAddress",
            addressLocality: address.city,
            addressRegion: address.region,
            addressCountry: address.country,
          },
        },
        knowsAbout: KNOWS_ABOUT[isEn ? "en" : "de"],
        knowsLanguage: ["de", "en"],
        sameAs: [social.linkedin, social.xing],
      },
    ],
  }

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="author" content={legalName} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}
      <meta name="theme-color" content="#163a6b" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={brand} />
      <meta property="og:locale" content={isEn ? "en_US" : "de_DE"} />
      <meta property="og:locale:alternate" content={isEn ? "de_DE" : "en_US"} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={t("header:portrait_alt")} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImage} />
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <script>{`window.plausible=window.plausible||function(){(window.plausible.q=window.plausible.q||[]).push(arguments)}`}</script>
      <script
        defer
        data-domain={analyticsDomain}
        src="https://plausible.io/js/script.js"
      ></script>
    </Helmet>
  )
}
